<template>
  <section id="customer-owners" class="mb-8 mt-2">
    <v-row class="d-flex justify-center">
      <v-col cols="12" md="12" lg="12">
        <card-widget>
          <template #cardBody>
            <v-row no-gutters align="start">
              <v-col cols="12" lg="3" md="3">
                <h5
                  class="
                    text-h6
                    secondary--font
                    font-weight-large
                    text--primary
                    pb-6
                  "
                >
                  {{ $dictionary.app.customers.owner.title }}
                </h5>
              </v-col>
              <v-col
                cols="12"
                lg="9"
                md="9"
                class="d-flex justify-space-between overflow-auto"
              >
                <v-spacer />
                <debounce-search-text-field
                  :lazy-search="getOwnerslist"
                  placeholder="Search owner..."
                  prop-to-search="email"
                  text-field-class=""
                  :sort-by="sortConfig.sortBy"
                  :sort-order="sortConfig.sortOrder"
                  @search-value="ownerSearch = $event"
                />

                <v-btn
                  outlined
                  @click="onDeleteOwner(true)"
                  class="font-weight-bold mx-2"
                  :disabled="!hasSelectedOwners"
                  :class="{
                    'btn-light-grey-border text--secondary': hasSelectedOwners,
                  }"
                >
                  <v-icon v-text="'mdi-delete'" class="pr-3 text--secondary" />
                  Delete
                </v-btn>

                <v-btn
                  outlined
                  @click="addOwnerDrawer = true"
                  class="btn-light-grey-border text--secondary font-weight-bold"
                >
                  <v-icon class="pr-3 text--secondary" v-text="'mdi-plus'" />
                  Add Owner
                </v-btn>
              </v-col>
            </v-row>
            <user-customers-table
              :data="owners"
              is-multi-select
              :update-record="updateRecord"
              :is-loading="isLoading"
              default-sort-order="email"
              :headers="headers"
              @get-records="getOwnerslist"
              :reset-selected-customers="resetSelectedOwners"
              @update-customers="selectedOwners = $event"
            />
          </template>
        </card-widget>
        <delete-record-dialog
          @delete="removeUserAccess"
          :is-loading="isOwnerDeleting"
          :title="removeAccessDialog.title"
          :dialog="removeAccessDialog.dialog"
          @close="removeAccessDialog.dialog = false"
          :sub-title="removeAccessDialog.subTitle"
        />
        <add-customer-owner-drawer
          :is-drawer-open="addOwnerDrawer"
          @update-records="updateRecords"
          @toogle-drawer="addOwnerDrawer = $event"
        />
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { default as headers } from "@/data/table-headers/customer-owners.headers.json";
import { CardWidget } from "@/components/widgets";
import { UserCustomersTable } from "@/components/tables";
import {
  getName,
  hasProp,
  isEmpty,
  isResponseCode,
} from "@/utils/common.utils";

import {
  DebounceSearchTextField,
  DeleteRecordDialog,
} from "@/components/shared";
import AddCustomerOwnerDrawer from "@/components/drawers/AddCustomerOwnerDrawer.vue";
import { deleteCustomerOwner, getCustomerOwners } from "@/services";
import { SharedUtilsMixin } from "@/mixins";
import { RESPONSE_CODES } from "@/constants";

/**
 * Customer owners tab
 */
export default {
  name: "CustomerOwners",
  /**
   * ---------------- Mixins ------------------
   */
  mixins: [SharedUtilsMixin],
  /**
   * ---------------- Components ------------------
   */
  components: {
    CardWidget,
    UserCustomersTable,
    DeleteRecordDialog,
    AddCustomerOwnerDrawer,
    DebounceSearchTextField,
  },
  /**
   * ---------------- Data properties ------------------
   */
  data() {
    const { deleteAccess } = this.$dictionary.app.customers.owner;

    return {
      headers,
      owners: [],
      isLoading: false,
      updateRecord: false,
      selectedOwners: [],
      addOwnerDrawer: false,
      removeAccessDialog: {
        dialog: false,
        title: deleteAccess.title,
        subTitle: deleteAccess.subTitle,
        multipleTitle: deleteAccess.multipleTitle,
      },
      isOwnerDeleting: false,
      ownerParams: {},
      ownerSearch: "",
      resetSelectedOwners: false,
    };
  },
  /**
   * ---------------- Computed properties ------------------
   */
  computed: {
    /**
     * Determines if user have selected owners or not
     * @type {Boolean}
     */
    hasSelectedOwners() {
      return !isEmpty(this.selectedOwners);
    },
    /**
     * Determines if the owners selected are more than 1
     * @type {Boolean}
     */
    hasMultipleOwners() {
      return this.selectedOwners?.length > 1;
    },
    /**
     * Customer owners listing sorting configuration
     */
    sortConfig() {
      return this.getSortConfig("ownerParams", "email");
    },
    /**
     * Id of the customer whose owners are being displayed
     * @type {String}
     */
    customerid() {
      const { id } = this.$route.params;
      return id;
    },
  },
  /**
   * ---------------- Methods ------------------
   */
  methods: {
    /**
     * Toogles updateRecord property value and updates
     * customer owner records in table
     */
    updateRecords() {
      this.updateRecord = !this.updateRecord;
    },
    /**
     * Handles delete dialog
     */
    onDeleteOwner(val) {
      const { deleteAccess } = this.$dictionary.app.customers.owner;
      if (val) {
        this.removeAccessDialog.title = !this.hasMultipleOwners
          ? deleteAccess.title.replace(
              "{{name}}",
              this.selectedOwners[0].first_name
                ? getName(this.selectedOwners[0])
                : this.selectedOwners[0].email
            )
          : this.removeAccessDialog.multipleTitle;
      }
      this.removeAccessDialog.dialog = val;
    },
    resetOwners() {
      this.resetSelectedOwners = !this.resetSelectedOwners;
    },
    /**
     * Remove owner access to selected customer accounts
     */
    async removeUserAccess() {
      try {
        if (isEmpty(this.selectedOwners)) return;
        this.isOwnerDeleting = true;

        const [{ id }] = this.selectedOwners;
        const { status } = await deleteCustomerOwner(this.customerid, id);

        if (isResponseCode(status, RESPONSE_CODES.success)) {
          this.setSnackbar({
            value: true,
            message: this.$dictionary.app.customers.owner.deleteAccess
              .successMsg,
            type: this.snackbarTypes.success,
          });
          this.resetOwners();
        }
        this.updateRecords();
      } finally {
        this.isOwnerDeleting = false;
        this.onDeleteOwner(false);
      }
    },
    /**
     * Fetches customer owners list
     * @param {Object} params Customer get api request params
     */
    async getOwnerslist(params = this.ownerParams) {
      try {
        if (this.ownerSearch && !hasProp(params, "q[email_cont]")) {
          params["q[email_cont]"] = this.ownerSearch;
        }
        this.ownerParams = params;
        this.isLoading = true;

        const { data } = await getCustomerOwners(this.customerid, params);
        this.owners = data;
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
